import { useCallback, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { InfoIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { Resolver, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useUser from '@/hooks/useUser';
import { cn } from '@/lib/utils';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { getUsageLimitsSchema } from '@/util/validation/rules/billing-rules';

const PERCENTAGE_OPTIONS = ['50%', '75%', '100%'];
const MAX_NOTIFICATION_EMAILS = 10;

type FormValues = { limit: string; percentages: string[]; emails: { value: string }[] };
const FORM_ID = 'usage-limits-form';

const parseJsonArray = (value: string | undefined): string[] => {
  if (!value) {
    return [];
  }
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const UsageLimitsFieldset = () => {
  const { t } = useTranslation();
  const { customer, updateCustomer, updateCustomerStatus } = useBilling();
  const { user } = useUser();

  const initialValues = useMemo(() => {
    const percentages = parseJsonArray(customer?.metadata?.notify_percentages);

    const emails = parseJsonArray(customer?.metadata?.notify_emails);
    return {
      limit: customer?.metadata?.usage_limit ?? '',
      percentages,
      emails:
        emails.length > 0 ? emails.map((e) => ({ value: e })) : [{ value: user?.email ?? '' }],
    };
  }, [customer?.metadata, user?.email]);

  const form = useForm<FormValues>({
    mode: 'onChange',
    values: initialValues,
    resolver: yupResolver(getUsageLimitsSchema()) as Resolver<FormValues>,
  });
  const {
    handleSubmit,
    control,
    formState: { isDirty, errors },
  } = form;

  const { fields: emailFields, append, remove } = useFieldArray({ control, name: 'emails' });

  const onSubmit = useCallback(
    (data: FormValues) => {
      const emails = data.emails.map((e) => e.value).filter(Boolean);
      updateCustomer({
        metadata: {
          usage_limit: data.limit,
          notify_percentages: JSON.stringify(data.percentages),
          notify_emails: JSON.stringify(emails),
        },
      });
    },
    [updateCustomer]
  );

  const handleAddEmail = useCallback(() => {
    append({ value: '' });
  }, [append]);

  return (
    <div className="flex flex-col gap-2">
      <h2 className="px-1.5 text-sm leading-none font-medium text-foreground-muted">
        {t('billing.usage_limits')}
      </h2>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={updateCustomerStatus === 'pending'}
            disabled={!isDirty}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <div className="flex flex-col gap-4">
              <FormDescription>{t('billing.usage_limits_subtitle')}</FormDescription>
              <FormField
                control={control}
                name="limit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('billing.usage_limit')}
                      <InfoTooltip
                        content={t('billing.usage_limit_tooltip')}
                        side="top"
                        size="large"
                      >
                        <InfoIcon className="size-3 text-foreground-muted" />
                      </InfoTooltip>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="percentages"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('billing.usage_limit_label')}</FormLabel>
                    <FormControl>
                      <div className="flex gap-2">
                        {PERCENTAGE_OPTIONS.map((percentage) => {
                          const isSelected = field.value?.includes(percentage);
                          return (
                            <Button
                              key={percentage}
                              type="button"
                              variant="outline-neutral"
                              size="sm"
                              className={cn(
                                'flex-1',
                                isSelected && 'border-primary bg-bg-info-soft text-primary'
                              )}
                              onClick={() => {
                                if (isSelected) {
                                  field.onChange(
                                    field.value.filter((p: string) => p !== percentage)
                                  );
                                } else {
                                  field.onChange([...(field.value ?? []), percentage]);
                                }
                              }}
                            >
                              {percentage}
                            </Button>
                          );
                        })}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {emailFields.map((emailField, index) => (
                <FormField
                  key={emailField.id}
                  control={control}
                  name={`emails.${index}.value`}
                  render={({ field }) => (
                    <FormItem>
                      {index === 0 && <FormLabel>{t('billing.emails')}</FormLabel>}
                      <FormControl>
                        <div className="flex items-center gap-2">
                          <Input type="email" placeholder={user?.email ?? ''} {...field} />
                          {emailFields.length > 1 && (
                            <Button
                              aria-label={t('common.remove')}
                              className="hover:text-destructive hover:[&_svg]:text-destructive"
                              type="button"
                              variant="ghost-neutral"
                              size="icon-md"
                              onClick={() => remove(index)}
                            >
                              <Trash2Icon size={16} />
                            </Button>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
              {errors.emails?.root?.message && (
                <FormMessage>{errors.emails.root.message}</FormMessage>
              )}
              {emailFields.length < MAX_NOTIFICATION_EMAILS && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="self-start"
                  onClick={handleAddEmail}
                >
                  <PlusIcon size={16} />
                  {t('billing.add_email')}
                </Button>
              )}
            </div>
          </FieldsetCard>
        </form>
      </Form>
    </div>
  );
};
